import { NestedEvent } from "@/types/serializers"
import { EventsIndexProps } from "@/types/types"
import Button from "@/components/Button"
import { MapPinIcon, PlusIcon } from "@heroicons/react/20/solid"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import SearchInput from "./SearchInput"
import { Modals, useModals } from "@/hooks/useModals"
import Pagination from "./Pagination"
import SortingSelector from "./SortingSelector"
import { Link, usePage } from "@inertiajs/react"
import { WP } from "./pieces"

const EventsList = () => {
  const { openModal } = useModals()
  const { events, filters } = usePage<EventsIndexProps>().props
  const search = filters.search || ""

  const hasNoEvents = !search && events.length === 0

  return (
    <div className="flex flex-1 flex-col pt-6">
      {hasNoEvents ? (
        <div className="padding mx-auto flex max-w-xs flex-col items-center justify-center py-10">
          <div className="flex items-center justify-center rounded-xl bg-gray-200 p-3">
            <WP className="h-6 w-6 text-gray-500" />
          </div>
          <h3 className="my-4 text-center text-base leading-6 font-semibold text-gray-900">
            Create your first event
          </h3>
          <Button size="sm" onClick={() => openModal(Modals.AddEvent)}>
            <PlusIcon className="h-5 w-5" />
            <span>Add event</span>
          </Button>
        </div>
      ) : (
        <>
          <div className="padding relative flex flex-col items-start justify-start gap-x-8 gap-y-4 sm:flex-row sm:items-center">
            <SearchInput />
            <SortingSelector type="events" />
          </div>

          <div className="relative mt-6 flex flex-1 flex-col">
            {events && events.length > 0 ? (
              <>
                <ul
                  role="list"
                  className="divide-y divide-gray-200 border-y border-gray-200"
                >
                  {events.map((event) => (
                    <EventItem key={event.id} {...event} />
                  ))}
                </ul>

                <div className="flex-1" />

                <Pagination type="event" />
              </>
            ) : (
              <div className="padding mx-auto flex max-w-xs flex-col items-center justify-center py-10">
                <div className="flex items-center justify-center rounded-xl bg-gray-200 p-3">
                  <MagnifyingGlassIcon className="h-6 w-6 text-gray-500" />
                </div>
                <h3 className="mt-4 text-center text-base leading-6 font-semibold text-gray-900">
                  No events found.
                </h3>
                <p className="text-center text-sm text-gray-700">
                  &ldquo;{search}&rdquo; did not match the name of any event.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default EventsList

const EventItem = ({ id, name, location, games_count }: NestedEvent) => (
  <Link href={`/events/${id}`} className="padding block hover:bg-gray-100">
    <li className="flex justify-between gap-x-6 py-5">
      <div className="flex min-w-0 gap-x-4">
        <div className="min-w-0 flex-auto">
          <p className="truncate text-base leading-6 font-semibold text-gray-900">
            {name}
          </p>
          <div className="mt-1 flex h-5 items-center gap-x-1">
            {location ? (
              <>
                <MapPinIcon className="text-chessdrop-500 -ml-0.5 h-5 w-5" />
                <p className="text-sm text-gray-600">{location}</p>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
        <p className="text-sm text-gray-600">
          {games_count} game{games_count === 1 ? "" : "s"}
        </p>
      </div>
    </li>
  </Link>
)
